import React, { useEffect, useState, useRef, useMemo, useCallback } from 'react';
import Axios from '../../api/axios';
import { asArray, filterArray } from '../../utils/arrayData.js';
import { getCached, setCached, clearCached, casRefCacheKey } from '../../utils/requestCache.js';
import DataGrid, { Paging, Editing, Column, LoadPanel, Scrolling, Lookup, Selection, Toolbar as GridToolbar, Item as GridToolbarItem } from '../BootstrapDataGrid';
import Scheduler, { View } from '../BootstrapScheduler';
import { Button as SingleButton, DateBox, Popup } from '../BootstrapControls';
import Form, { GroupItem, SimpleItem, Label } from '../BootstrapForm';
import notify from '../../utils/notifyUtil';
import Dict from '../../messages.js';
import * as glb from '../../globals.js';
import { emitCariSync, subscribeCariSync } from '../../utils/cariSyncBus.js';
import { addMaxLengthToEditorOptions, addMaxLengthToGridColumn } from '../../utils/formValidationHelper.js';
import { attachFormEnterToNextField } from '../../utils/formEnterFocusNext';
import { ISLEMSABLON_LKP_EVENT, filterIslemSablonBySatis, pickSablonForSatisTuru, refreshGlobalIslemsablonLkp, sablonComboItems, sablonLinesByName } from '../../utils/islemsablonLkp';
import { buildCiDetayFromSablon, parseCiDetayWithMeta } from '../../utils/islemDetay';
import { buildIslemInsertsFromSablon, hasAnyCompletedIslem, recalcIslemRandevuUpdates, SABLON_COMPLETED_BLOCK_MESSAGE } from '../../utils/islemSablonDates';
import IslemGerceklestiCell from './IslemGerceklestiCell';
import {
    confirmRandevuOteleme,
    formatRandevuShiftMessage,
    mergeIslemUpdates,
    nextRandevuFromLastRow,
    planIslemDateShift,
    randevuForSablonTur,
} from '../../utils/islemRandevuShift';
import { gridHeightByRowCount, gridMaxHeightForRows } from '../../utils/gridAutoHeight.js';
import { canDeleteByOwner, islemIsYapanRef } from '../../utils/ownRecordDelete.js';

function sablonNameOnRows(rows, selectedSablon) {
    const picked = String(selectedSablon ?? '').trim();
    if (picked) return picked;
    for (const row of rows || []) {
        const name = parseCiDetayWithMeta(row?.CI_DETAY).sablonAd;
        if (name) return name;
    }
    return '';
}

function paketIcerikForSablon(sabitRows, stTur, sablonLines, sablonName) {
    const all = filterArray(sabitRows, (row) => row.ST_TUR === stTur);
    const name = String(sablonName ?? '').trim();
    if (!name) return all;
    const order = [];
    const seen = new Set();
    (sablonLines || [])
        .filter((line) => line && String(line.IS_AD ?? '').trim() === name)
        .sort((a, b) => (Number(a.IS_SIRA) || 0) - (Number(b.IS_SIRA) || 0))
        .forEach((line) => {
            const kod = Number(line.IS_TUR);
            if (!(kod > 0) || seen.has(kod)) return;
            seen.add(kod);
            order.push(kod);
        });
    if (!order.length) return all;
    const byKod = new Map(all.map((row) => [Number(row.ST_KODINT), row]));
    const items = order.map((kod) => byKod.get(kod)).filter(Boolean);
    return items.length ? items : all;
}

function satisTuruLabel(satisTuru) {
    const id = Number(satisTuru);
    if (!(id > 0)) return '';
    const row = (glb.default?.sabitLkp || []).find((item) => Number(item?.ST_REF) === id);
    return String(row?.ST_AD ?? '').trim();
}

export default function IslemPanel({ carRef, casRef, satistipigrubu, satisTipi, satisTuru, sablonRequest, onSablonRequestConsumed, islemReadOnly, islemRefreshToken = 0, gridVisibleRowCount = null, gridHeight = null }) {
    const [islemList, setIslemList] = useState([]);
    const [islemsablonLkp, setIslemsablonLkp] = useState(() => glb.default.islemsablonLkp || { detay: [], baslik: [] });
    const [selectedSablon, setSelectedSablon] = useState(null);
    const [showAllSablon, setShowAllSablon] = useState(false);
    const showAllSablonRef = useRef(false);
    showAllSablonRef.current = showAllSablon;
    const [sablonOffer, setSablonOffer] = useState(null);
    const saleSablonItems = useMemo(
        () => sablonComboItems(islemsablonLkp?.baslik, satisTipi, satisTuru, false)
            .filter((row) => String(row?.IS_AD ?? '').trim()),
        [islemsablonLkp, satisTipi, satisTuru]
    );
    const [sablonApplying, setSablonApplying] = useState(false);
    const sablonApplyLock = useRef(false);
    const currentSale = useRef(casRef);
    currentSale.current = casRef;
    useEffect(() => {
        currentSale.current = casRef;
        return () => { currentSale.current = null; };
    }, [casRef]);

    useEffect(() => {
        if (!sablonRequest || Number(sablonRequest.casRef) !== Number(casRef)) return;
        let cancelled = false;
        refreshGlobalIslemsablonLkp().then((lookup) => {
            if (cancelled) return;
            const items = sablonComboItems(lookup?.baslik, satisTipi, satisTuru, false)
                .filter((row) => String(row?.IS_AD ?? '').trim());
            const names = items.map((row) => String(row.IS_AD).trim());
            const preferred = pickSablonForSatisTuru(items, satisTuruLabel(satisTuru));
            if (names.length) setSablonOffer({ names, selected: preferred || names[0] });
            onSablonRequestConsumed?.();
        }).catch(() => {
            if (cancelled) return;
            notify('Satış kaydedildi ancak bağlı şablonlar yüklenemedi. İşlemler bölümünden tekrar deneyebilirsiniz.', 'warning', 4000);
            onSablonRequestConsumed?.();
        });
        return () => { cancelled = true; };
    }, [sablonRequest, casRef, satisTipi, satisTuru, onSablonRequestConsumed]);
    const [gridReadOnly, setGridReadOnly] = useState(true); // Satış gibi: true=Ekle/Düzenle/Yenile, false=Kaydet/Vazgeç
    const gridRef = useRef(null);
    const [gorunum, setGorunum] = useState('liste'); // 'liste' | 'takvim'
    const [takvimView, setTakvimView] = useState('year'); // 'month' | 'week' | 'day' | 'year'
    const [schedulerDate, setSchedulerDate] = useState(null); // Takvim başlangıç tarihi (ilk randevu ayı)
    const lastReqIdRef = useRef(0); // ardışık fetch'lerde geç kalmış cevapların state'i bozmasını engelle
    const [activeIslem, setActiveIslem] = useState(null);
    const [formReadOnly, setFormReadOnly] = useState(true);
    const [formDirty, setFormDirty] = useState(false);
    const [newFormToken, setNewFormToken] = useState(0);
    const isFormRecordNew = !activeIslem || Number(activeIslem.CI_REF) <= 0;
    const [focusedIslemKey, setFocusedIslemKey] = useState(null);
    const [selectedIslemKeys, setSelectedIslemKeys] = useState([]);
    const [isCreatingNew, setIsCreatingNew] = useState(false);
    const createLockRef = useRef(false);
    const enterNavCleanupRef = useRef(null);
    const attachEnterNavRoot = useCallback((node) => {
        if (enterNavCleanupRef.current) {
            enterNavCleanupRef.current();
            enterNavCleanupRef.current = null;
        }
        if (node) {
            enterNavCleanupRef.current = attachFormEnterToNextField(node);
        }
    }, []);
    useEffect(() => () => {
        if (enterNavCleanupRef.current) {
            enterNavCleanupRef.current();
            enterNavCleanupRef.current = null;
        }
    }, []);
    const displayIslemList = React.useMemo(() => {
        const rows = isCreatingNew && activeIslem && isFormRecordNew
            ? [...(islemList || []), activeIslem]
            : [...(islemList || [])];
        return rows.sort((a, b) => {
            const at = a?.CI_RAN_TAR ? new Date(a.CI_RAN_TAR).getTime() : Number.POSITIVE_INFINITY;
            const bt = b?.CI_RAN_TAR ? new Date(b.CI_RAN_TAR).getTime() : Number.POSITIVE_INFINITY;
            return at - bt || Number(a?.CI_REF || 0) - Number(b?.CI_REF || 0);
        });
    }, [islemList, isCreatingNew, activeIslem, isFormRecordNew]);

    // Takvim için: paket içeriği metni (ST_AD) ve ilk randevu ayı
    const sabitMap = useMemo(() => {
        try {
            const arr = filterArray(glb.default.sabitLkp, x => (satistipigrubu == null || x.ST_TUR === satistipigrubu));
            const mp = {};
            arr.forEach(s => { mp[s.ST_KODINT] = s.ST_AD; });
            return mp;
        } catch {
            return {};
        }
    }, [satistipigrubu]);

    const schedulerData = useMemo(() => {
        const startOfDay = (val) => {
            const d = new Date(val);
            if (isNaN(d)) return null;
            return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 0, 0, 0, 0);
        };
        const endOfDay = (val) => {
            const d = new Date(val);
            if (isNaN(d)) return null;
            return new Date(d.getFullYear(), d.getMonth(), d.getDate(), 23, 59, 59, 999);
        };

        return (islemList || [])
            .filter(r => r && r.CI_RAN_TAR)
            .map(r => {
                const sd = startOfDay(r.CI_RAN_TAR);
                return {
                    ...r,
                    paketText: sabitMap[r.CI_TUR] || '',
                    startDate: sd,               // Gün başlangıcı 00:00
                    endDate: endOfDay(sd),       // Aynı gün 23:59:59.999
                    allDay: true                 // Tümü gün olarak işaretle
                };
            });
    }, [islemList, sabitMap]);
    const applyIslemChanges = React.useCallback((baseList, changes = {}) => {
        const toNum = (v) => Number(v || 0);
        const inserted = Array.isArray(changes.inserted) ? changes.inserted : [];
        const updated = Array.isArray(changes.updated) ? changes.updated : [];
        const deleted = Array.isArray(changes.deleted) ? changes.deleted : [];
        const islemRef = (row) => toNum(row?.CI_REF ?? row?.ci_ref ?? row);
        const deletedSet = new Set(deleted.map((x) => islemRef(x)));
        let next = (Array.isArray(baseList) ? baseList : []).filter((row) => !deletedSet.has(islemRef(row)));
        const upsert = (row) => {
            const id = islemRef(row);
            if (!id) return;
            const idx = next.findIndex((r) => islemRef(r) === id);
            if (idx >= 0) {
                next[idx] = { ...next[idx], ...row };
            } else {
                next.unshift(row);
            }
        };
        inserted.forEach(upsert);
        updated.forEach(upsert);
        next = next.sort((a, b) => islemRef(b) - islemRef(a));
        return next;
    }, []);

    const firstApptMonth = useMemo(() => {
        if (!schedulerData || schedulerData.length === 0) return new Date();
        const dates = schedulerData.map(i => new Date(i.CI_RAN_TAR)).filter(d => !isNaN(d));
        if (dates.length === 0) return new Date();
        dates.sort((a, b) => a - b);
        return new Date(dates[0].getFullYear(), dates[0].getMonth(), 1);
    }, [schedulerData]);

    // İlk yüklemede schedulerDate set et (view değişimlerinde ay'a geri dönmeyi engeller)
    useEffect(() => {
        if (!schedulerDate && firstApptMonth) {
            setSchedulerDate(firstApptMonth);
        }
    }, [schedulerDate, firstApptMonth]);

    const fetchIslemList = React.useCallback((forcedCasRef, forceReload = false) => {
        const targetCasRef = (typeof forcedCasRef === 'number') ? forcedCasRef : casRef;
        const myReqId = ++lastReqIdRef.current;
        if (targetCasRef == null || typeof targetCasRef !== 'number' || targetCasRef <= 0) {
            setIslemList([]);
            return;
        }
        const cacheKey = casRefCacheKey('ISLEMLIST', targetCasRef);
        if (forceReload) {
            clearCached(cacheKey);
        }
        const cached = forceReload ? null : getCached(cacheKey);
            if (cached && Array.isArray(cached)) {
            if (myReqId !== lastReqIdRef.current) return;
            setIslemList(cached);
            return;
        }
        Axios.get(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/islemlist?CAS_REF=${targetCasRef}`)
            .then(res => {
                // Sadece en güncel isteğin sonucu işlensin (geciken eski yanıtlar görmezden gelinir)
                if (myReqId !== lastReqIdRef.current) return;
                if (res.data) {
                    setCached(cacheKey, res.data);
                    setIslemList(res.data);
                } else {
                    setIslemList([]);
                }
            }).catch(err => {
                if (myReqId !== lastReqIdRef.current) return; // önceki isteklerin hatalarını yoksay
                console.log(err);
                notify(Dict.get('serverError') + err, 'error', 2000);
            });
    }, [casRef]); // casRef değişince callback yeniden oluşturulur, böylece her satırda doğru CAS_REF fetch edilir

    useEffect(() => {
        setGridReadOnly(true);
        setFormReadOnly(true);
        setFormDirty(false);
        setIsCreatingNew(false);
        createLockRef.current = false;
    }, [casRef]);

    useEffect(() => {
        // Satış/tip değiştiğinde önceki satıştan kalan şablon seçilemesin.
        setSelectedSablon(null);
        setSablonOffer(null);
    }, [casRef, satisTipi, satisTuru]);

    useEffect(() => {
        // Satış değişince veya dış sync (İşlem Gir/Satış sekmesinden değişiklik) ile yenile
        setSchedulerDate(null);
        if (islemRefreshToken > 0 && casRef) clearCached(casRefCacheKey('ISLEMLIST', casRef));
        fetchIslemList();
    }, [casRef, fetchIslemList, islemRefreshToken]);

    useEffect(() => {
        if (!formReadOnly || isCreatingNew || createLockRef.current) return;
        if (!Array.isArray(islemList) || islemList.length === 0) {
            setActiveIslem(null);
            setFocusedIslemKey(null);
            return;
        }
        if (!activeIslem || !islemList.some(x => Number(x.CI_REF) === Number(activeIslem.CI_REF))) {
            setActiveIslem(islemList[0]);
            setFocusedIslemKey(islemList[0]?.CI_REF ?? null);
            setFormReadOnly(true);
            setFormDirty(false);
        }
    }, [islemList, activeIslem, formReadOnly, isCreatingNew]);

    const patchIslemRow = useCallback((ciRef, patch) => {
        const id = Number(ciRef || 0);
        if (!id) return;
        setIslemList((prev) => prev.map((r) => (Number(r.CI_REF) === id ? { ...r, ...patch } : r)));
        setActiveIslem((prev) => (prev && Number(prev.CI_REF) === id ? { ...prev, ...patch } : prev));
    }, []);

    useEffect(() => {
        const syncFromGlobal = () => {
            const lkp = glb.default.islemsablonLkp;
            if (lkp) setIslemsablonLkp(lkp);
        };

        syncFromGlobal();
        let cancelled = false;
        refreshGlobalIslemsablonLkp()
            .then((data) => {
                if (!cancelled && data) setIslemsablonLkp(data);
            })
            .catch((err) => {
                if (cancelled) return;
                console.log(err);
                notify(Dict.get('serverError') + err, 'error', 2000);
            });

        window.addEventListener(ISLEMSABLON_LKP_EVENT, syncFromGlobal);
        return () => {
            cancelled = true;
            window.removeEventListener(ISLEMSABLON_LKP_EVENT, syncFromGlobal);
        };
    }, [satisTipi, satisTuru]);
    useEffect(() => {
        const unsub = subscribeCariSync((evt) => {
            if (!carRef || Number(evt?.carRef) !== Number(carRef)) return;

            if (evt?.source === 'satis' && evt?.tedaviTarihRecalc != null && Number(evt?.casRef) === Number(casRef)) {
                void recalcFromTedaviTarihRef.current?.(evt.tedaviTarihRecalc);
                return;
            }

            if (evt?.source !== 'islem') return;
            const evtCasRef = Number(evt?.casRef || 0);
            if (evtCasRef && Number(evtCasRef) !== Number(casRef)) return;
            const changes = evt?.islemChanges;
            if (changes && (Array.isArray(changes.inserted) || Array.isArray(changes.updated) || Array.isArray(changes.deleted))) {
                setIslemList((prev) => {
                    const next = applyIslemChanges(prev, changes);
                    if (casRef) setCached(casRefCacheKey('ISLEMLIST', casRef), next);
                    setActiveIslem((cur) => {
                        if (!cur) return cur;
                        const ref = Number(cur.CI_REF ?? cur.ci_ref ?? 0);
                        const updated = next.find((r) => Number(r.CI_REF ?? r.ci_ref) === ref);
                        return updated ? { ...cur, ...updated } : cur;
                    });
                    return next;
                });
                return;
            }
            if (casRef) clearCached(casRefCacheKey('ISLEMLIST', casRef));
            fetchIslemList();
        });
        return () => unsub();
    }, [carRef, casRef, applyIslemChanges, fetchIslemList]);

    const applySablon = async (sablonName = selectedSablon) => {
        if (sablonApplyLock.current || islemReadOnly) return;
        sablonApplyLock.current = true;
        setSablonApplying(true);
        try {
        const lookup = await refreshGlobalIslemsablonLkp();
        if (currentSale.current !== casRef) return;
        const detay = lookup?.detay || [];
        if (!sablonName || !detay.length || casRef == null || casRef <= 0) {
            notify('Şablon uygulanamadı: seçim veya satış kaydı geçersiz.', 'warning', 2000);
            return;
        }
        const secili = sablonLinesByName(detay, sablonName, satisTipi, satisTuru, showAllSablonRef.current);
        if (!secili || secili.length === 0) {
            notify('Seçilen şablon satış tipi/türü ile eşleşmiyor veya detay bulunamadı.', 'warning', 2500);
            return;
        }

        // Panel önbelleği eski kalmış olabilir. Mükerrer kontrolünü ve ekleme
        // başlangıcını mutlaka sunucudaki güncel liste üzerinden yap.
        const existingResponse = await Axios.get(
            `${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/islemlist?CAS_REF=${casRef}`,
            { params: { sablonRefresh: Date.now() } }
        );
        if (currentSale.current !== casRef) return;
        if (!Array.isArray(existingResponse.data)) throw new Error('Mevcut işlemler yüklenemedi.');
        const existingSaved = existingResponse.data.filter((r) => Number(r?.CI_REF ?? 0) > 0);
        setIslemList(existingResponse.data);
        setCached(casRefCacheKey('ISLEMLIST', casRef), existingResponse.data);
        const toDelete = [];
        let baslangicTar = new Date();
        if (hasAnyCompletedIslem(existingSaved)) {
            window.alert(SABLON_COMPLETED_BLOCK_MESSAGE);
            return;
        }
        if (existingSaved.length > 0) {
            const clearAndApply = window.confirm('Önceki işlemler silinecek. Emin misiniz?');
            if (!clearAndApply) return;
            existingSaved.forEach((row) => {
                const ref = Number(row?.CI_REF ?? 0);
                if (ref > 0) toDelete.push(ref);
            });
        }

        const sablonSirali = [...secili].sort((a, b) => (a.IS_SIRA || 0) - (b.IS_SIRA || 0));
        const yeni = buildIslemInsertsFromSablon(
            sablonSirali,
            baslangicTar,
            buildCiDetayFromSablon,
            sablonName,
            null
        );

        // Sunucuya gönder (tamamen replace mantığı)
        const changeGroups = {
            inserted: yeni,
            updated: [],
            deleted: toDelete
        };

        const ok = await saveIslem(changeGroups);
        if (ok) {
            gridRef.current?.instance?.cancelEditData();
            notify(
                toDelete.length > 0
                    ? 'Eski işlemler silindi, şablon baştan uygulandı.'
                    : 'Şablon uygulandı ve kaydedildi.',
                'success',
                2000
            );
        } else {
            notify('Şablon uygulanırken hata oluştu.', 'error', 2500);
        }
        } catch (err) {
            console.error('applySablon:', err);
            notify('Şablon uygulanırken hata oluştu: ' + (err?.message || err), 'error', 3000);
        } finally {
            sablonApplyLock.current = false;
            if (currentSale.current === casRef) setSablonApplying(false);
        }
    };

    const saveIslem = async (changes) => {
        try {
            const res = await Axios.post(`${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/islemkaydet?cid=${glb.default.KULLANICI.KUL_DEF_FIR_REF}`, {
                inserted: changes.inserted || [],
                updated: changes.updated || [], 
                deleted: changes.deleted || [],
                casRef: casRef,
                carRef: carRef,
                kulRef: glb.default.KULLANICI.KUL_REF
            });
            if (res.data && res.data.success) {
                const insertedRows = Array.isArray(res?.data?.inserted) ? res.data.inserted : (changes.inserted || []);
                const updatedRows = Array.isArray(res?.data?.updated) ? res.data.updated : (changes.updated || []);
                const deletedRows = Array.isArray(res?.data?.deleted) ? res.data.deleted : (changes.deleted || []);
                const responseIsIncomplete = insertedRows.length < (changes.inserted || []).length
                    || updatedRows.length < (changes.updated || []).length
                    || deletedRows.length < (changes.deleted || []).length;
                if (responseIsIncomplete) {
                    const freshResponse = await Axios.get(
                        `${process.env.REACT_APP_BACKEND_SERVER}CARI_SATIS/islemlist?CAS_REF=${casRef}`,
                        { params: { kayitRefresh: Date.now() } }
                    );
                    if (!Array.isArray(freshResponse.data)) throw new Error('Kaydedilen işlemler doğrulanamadı.');
                    if (currentSale.current !== casRef) return false;
                    setIslemList(freshResponse.data);
                    setCached(casRefCacheKey('ISLEMLIST', casRef), freshResponse.data);
                    emitCariSync({ carRef, casRef, source: 'islem' });
                    notify('Değişiklikler başarıyla kaydedildi.', 'success', 2000);
                    return true;
                }
                notify('Değişiklikler başarıyla kaydedildi.', 'success', 2000);
                setIslemList((prev) => {
                    const nextList = applyIslemChanges(prev, {
                        inserted: insertedRows,
                        updated: updatedRows,
                        deleted: deletedRows,
                    });
                    if (casRef) setCached(casRefCacheKey('ISLEMLIST', casRef), nextList);
                    const islemRef = (row) => Number(row?.CI_REF ?? row?.ci_ref ?? row ?? 0);
                    const deletedSet = new Set((deletedRows || []).map((x) => islemRef(x)));
                    const currentRef = islemRef(activeIslem);
                    if (deletedSet.has(currentRef)) {
                        const first = nextList[0] || null;
                        setActiveIslem(first);
                        setFocusedIslemKey(first?.CI_REF ?? first?.ci_ref ?? null);
                    } else {
                        const latest = insertedRows[0] || updatedRows.find((r) => islemRef(r) === currentRef) || null;
                        if (latest) {
                            setActiveIslem(latest);
                            setFocusedIslemKey(latest?.CI_REF ?? latest?.ci_ref ?? null);
                        }
                    }
                    return nextList;
                });
                emitCariSync({
                    carRef,
                    casRef,
                    source: 'islem',
                    islemChanges: { inserted: insertedRows, updated: updatedRows, deleted: deletedRows },
                });
                if ((changes.deleted || []).length > 0) setSelectedIslemKeys([]);
                return true;
            }
            notify(res?.data?.message || res?.data?.error || 'Değişiklikler kaydedilemedi.', 'error', 2500);
            return false;
        } catch (err) {
            console.log(err);
            notify(Dict.get('serverError') + err, 'error', 2000);
            return false;
        }
    };

    const recalcFromTedaviTarihRef = useRef(null);
    recalcFromTedaviTarihRef.current = async (tedaviTar) => {
        if (!tedaviTar || !casRef || casRef <= 0) return;
        const rows = (islemList || []).filter((r) => Number(r?.CI_REF ?? r?.ci_ref ?? 0) > 0);
        if (!rows.length) return;
        if (hasAnyCompletedIslem(rows)) return;

        let sablonAd = '';
        for (const r of rows) {
            const { sablonAd: ad } = parseCiDetayWithMeta(r?.CI_DETAY);
            if (ad) {
                sablonAd = ad;
                break;
            }
        }

        const detay = filterIslemSablonBySatis(islemsablonLkp?.detay, satisTipi, satisTuru);
        let secili = sablonAd
            ? filterArray(detay, (x) => x.IS_AD === sablonAd)
            : [];
        if (!secili.length && satisTipi != null) {
            const turSet = new Set(rows.map((r) => Number(r.CI_TUR)));
            secili = filterArray(detay, (x) => Number(x.IS_TIP) === Number(satisTipi) && turSet.has(Number(x.IS_TUR)));
        }
        if (!secili.length) return;

        const updates = recalcIslemRandevuUpdates(rows, secili, tedaviTar);
        if (!updates.length) return;

        const ok = await saveIslem({ inserted: [], updated: updates, deleted: [] });
        if (ok) {
            notify('Tedavi tarihi değişti; işlem randevuları yeniden hesaplandı.', 'info', 2500);
        }
    };

    const persistIslemUpdatesFromGrid = useCallback(async (sourceRow, updates) => {
        const rows = (updates || []).filter((u) => Number(u?.CI_REF ?? 0) > 0);
        if (!rows.length) return false;
        setIslemList((prev) => mergeIslemUpdates(prev, rows));
        const key = Number(sourceRow?.CI_REF ?? 0);
        const selfPatch = rows.find((u) => Number(u.CI_REF) === key);
        if (selfPatch) {
            patchIslemRow(key, selfPatch);
            setNewFormToken((v) => v + 1);
        }
        return saveIslem({ inserted: [], updated: rows, deleted: [] });
    }, [patchIslemRow]);

    const applyFormDateChange = useCallback(async (dataField, newValue, previousValue) => {
        const row = activeIslem;
        if (!row) return;
        const plan = planIslemDateShift(islemList, row, dataField, newValue, previousValue, { casRef });
        if (plan.needsConfirm && !confirmRandevuOteleme(plan.deltaDays)) {
            setActiveIslem((prev) => (prev ? { ...prev, [dataField]: previousValue } : prev));
            setNewFormToken((v) => v + 1);
            return;
        }
        setActiveIslem((prev) => (prev ? { ...prev, [dataField]: newValue } : prev));
        const key = Number(row?.CI_REF ?? 0);
        if (key > 0) {
            const byRef = new Map();
            (plan.updates || []).forEach((u) => byRef.set(Number(u.CI_REF), { ...byRef.get(Number(u.CI_REF)), ...u }));
            byRef.set(key, { ...byRef.get(key), CI_REF: key, [dataField]: newValue });
            const allUpdates = Array.from(byRef.values()).filter((u) => u.CI_REF > 0);
            if (plan.updates?.length) {
                setIslemList((prev) => mergeIslemUpdates(prev, allUpdates));
                notify(formatRandevuShiftMessage(plan.shiftedCount, plan.deltaDays), 'info', 2000);
            }
            await saveIslem({ inserted: [], updated: allUpdates, deleted: [] });
            setNewFormToken((v) => v + 1);
        } else if (plan.updates?.length) {
            setIslemList((prev) => mergeIslemUpdates(prev, plan.updates));
            notify(formatRandevuShiftMessage(plan.shiftedCount, plan.deltaDays), 'info', 2000);
        }
        setFormDirty(true);
        setGridReadOnly(false);
    }, [activeIslem, islemList, casRef]);

    const isNewUnsavedRecord = !casRef || casRef === -1 || casRef <= 0;

    const resolvedGridHeight = React.useMemo(() => {
        const maxFromProp = Number(gridHeight) > 0
            ? Number(gridHeight)
            : (Number(gridVisibleRowCount) > 0
                ? gridMaxHeightForRows(gridVisibleRowCount)
                : 600);
        const dataCount = (displayIslemList || []).filter((r) => Number(r?.CI_REF) > 0 || r?.CI_REF === -1).length;
        return gridHeightByRowCount(dataCount, maxFromProp);
    }, [gridHeight, gridVisibleRowCount, displayIslemList]);

    return (
        <div ref={attachEnterNavRoot} style={{ width: '100%' }}>
            <Popup visible={!!sablonOffer} title="İşlem şablonu onayı" showTitle={true}
                showCloseButton={true} onHiding={() => setSablonOffer(null)}
                width={460} height="auto" closeOnOutsideClick={false}>
                <p>Satış kaydedildi. Seçilen satış türüne bağlı işlem şablonu uygulansın mı?</p>
                <label htmlFor={`sablon-offer-${casRef}`}>İşlem şablonu</label>
                <select id={`sablon-offer-${casRef}`} className="form-select mb-3"
                    value={saleSablonItems.some((row) => row.IS_AD === sablonOffer?.selected) ? sablonOffer.selected : (saleSablonItems[0]?.IS_AD || '')}
                    onChange={(e) => setSablonOffer((offer) => ({ ...offer, selected: e.target.value }))}>
                    {saleSablonItems.map((row) => <option key={row.IS_AD} value={row.IS_AD}>{row.IS_AD}</option>)}
                </select>
                <SingleButton text="Evet, uygula" type="success" disabled={sablonApplying || islemReadOnly}
                    onClick={() => {
                        const name = sablonOffer?.selected;
                        setSablonOffer(null);
                        setSelectedSablon(name);
                        void applySablon(name);
                    }} />
                <SingleButton text="Hayır" onClick={() => setSablonOffer(null)} />
            </Popup>
            <div style={{ 
                marginBottom: '8px', 
                fontWeight: 'bold', 
                fontSize: '14px',
                color: 'var(--dx-color-text, #212529)',
                borderBottom: '2px solid var(--dx-color-primary, #1976d2)',
                paddingBottom: '4px',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: 8
            }}>
                <span>🔧 {Dict.get('İşlemler')}</span>
                {!isNewUnsavedRecord && (
                    <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                        <SingleButton
                            text={gorunum === 'liste' ? 'Takvim' : 'Liste'}
                            icon={gorunum === 'liste' ? 'event' : 'bulletlist'}
                            type="normal"
                            stylingMode="contained"
                            onClick={() => setGorunum(g => g === 'liste' ? 'takvim' : 'liste')}
                        />
                        {gorunum === 'liste' && gridReadOnly && (
                            <>
                                <SingleButton text={Dict.get('Yeni Kayıt')} icon="plus" type="default" onClick={() => {
                                    createLockRef.current = true;
                                    setFocusedIslemKey(null);
                                    setNewFormToken(v => v + 1);
                                    const sablonName = sablonNameOnRows(islemList, selectedSablon);
                                    setActiveIslem({
                                        CI_REF: -1,
                                        CI_TUR: null,
                                        CI_RAN_TAR: nextRandevuFromLastRow(islemList, islemsablonLkp?.detay),
                                        CI_ISL_TAR: null,
                                        CI_SEANS_SAY: null,
                                        CI_ACK: '',
                                        CI_DETAY: sablonName ? { sablonAd: sablonName, items: [] } : [],
                                    });
                                    setFormReadOnly(false);
                                    setFormDirty(false);
                                    setGridReadOnly(false);
                                    setIsCreatingNew(true);
                                }} disabled={islemReadOnly} />
                                <SingleButton text={Dict.get('Düzenle')} icon="edit" type="default" onClick={() => { createLockRef.current = false; setFormReadOnly(false); setGridReadOnly(false); setIsCreatingNew(false); }} disabled={islemReadOnly || !activeIslem || (Number(activeIslem.CI_REF) > 0 && !canDeleteByOwner(glb.default?.KULLANICI, islemIsYapanRef(activeIslem)))} />
                                <SingleButton text={Dict.get('Yenile')} icon="refresh" type="default" onClick={() => { fetchIslemList(undefined, true); setGridReadOnly(true); }} />
                                {activeIslem && Number(activeIslem.CI_REF) > 0
                                    && canDeleteByOwner(glb.default?.KULLANICI, islemIsYapanRef(activeIslem)) && (
                                    <SingleButton text="Sil" icon="trash" type="danger" onClick={async () => {
                                        if (!window.confirm('Bu işlem silinsin mi?')) return;
                                        await saveIslem({ inserted: [], updated: [], deleted: [activeIslem.CI_REF] });
                                    }} />
                                )}
                            </>
                        )}
                        {gorunum === 'liste' && !gridReadOnly && (
                            <>
                                <SingleButton text={Dict.get('Kaydet')} icon="save" type="success" onClick={async () => {
                                    if (!activeIslem) return;
                                    const isNew = Number(activeIslem.CI_REF) <= 0;
                                    const success = await saveIslem({
                                        inserted: isNew ? [activeIslem] : [],
                                        updated: isNew ? [] : [activeIslem],
                                        deleted: []
                                    });
                                    if (success) {
                                        createLockRef.current = false;
                                        setGridReadOnly(true);
                                        setFormReadOnly(true);
                                        setFormDirty(false);
                                        setIsCreatingNew(false);
                                    }
                                }} disabled={!isFormRecordNew && !formDirty} />
                                <SingleButton text={Dict.get('Vazgeç')} icon="undo" type="warning" onClick={() => { createLockRef.current = false; fetchIslemList(); setGridReadOnly(true); setFormReadOnly(true); setFormDirty(false); setIsCreatingNew(false); }} />
                            </>
                        )}
                    </div>
                )}
            </div>
            {isNewUnsavedRecord && (
                <div style={{ padding: 8, margin: '8px 0', background: '#fff3cd', border: '1px solid #ffc107', borderRadius: 4, color: '#856404' }}>
                    ℹ️ İşlem eklemek için önce satış kaydını kaydedin.
                </div>
            )}
            {gorunum === 'liste' && !isNewUnsavedRecord && activeIslem && (
                <Form
                    key={`islem-form-${activeIslem?.CI_REF ?? 'new'}-${newFormToken}`}
                    formData={activeIslem}
                    readOnly={formReadOnly || islemReadOnly}
                    colCount={1}
                    onFieldDataChanged={(e) => {
                        if (e.dataField === 'CI_ISL_TAR' || e.dataField === 'CI_RAN_TAR') {
                            const prevVal = activeIslem?.[e.dataField];
                            void applyFormDateChange(e.dataField, e.value, prevVal);
                            return;
                        }
                        if (e.dataField === 'CI_TUR' && Number(activeIslem?.CI_REF) <= 0) {
                            const sablonName = sablonNameOnRows(islemList, selectedSablon);
                            const fromDef = randevuForSablonTur(islemList, islemsablonLkp?.detay, sablonName, e.value);
                            setActiveIslem((prev) => ({
                                ...prev,
                                CI_TUR: e.value,
                                CI_RAN_TAR: fromDef || prev?.CI_RAN_TAR,
                                CI_DETAY: sablonName ? { sablonAd: sablonName, items: [] } : (prev?.CI_DETAY || []),
                            }));
                            setFormDirty(true);
                            setGridReadOnly(false);
                            return;
                        }
                        setActiveIslem(prev => ({ ...prev, [e.dataField]: e.value }));
                        setFormDirty(true);
                        setGridReadOnly(false);
                    }}
                >
                    <GroupItem colCount={2}>
                        <SimpleItem dataField="CI_TUR" editorType="dxSelectBox" editorOptions={{ items: paketIcerikForSablon(glb.default.sabitLkp, satistipigrubu, islemsablonLkp?.detay, sablonNameOnRows(islemList, selectedSablon)), lockItems: true, searchEnabled: true, searchMode: 'contains', displayExpr: 'ST_AD', searchExpr: 'ST_AD', valueExpr: 'ST_KODINT' }}><Label text={Dict.get('Paket İçeriği')} /></SimpleItem>
                        <SimpleItem dataField="CI_RAN_TAR" editorType="dxDateBox"><Label text={Dict.get('Randevu Tarihi')} /></SimpleItem>
                        <SimpleItem
                            render={() => (
                                <DateBox
                                    value={activeIslem?.CI_ISL_TAR ?? null}
                                    readOnly={islemReadOnly}
                                    disabled={islemReadOnly}
                                    type="datetime"
                                    displayFormat="dd-MM-yyyy HH:mm"
                                    onValueChanged={(e) => {
                                        const prevVal = activeIslem?.CI_ISL_TAR;
                                        if (e.value === prevVal || (e.value == null && prevVal == null)) return;
                                        void applyFormDateChange('CI_ISL_TAR', e.value, prevVal);
                                    }}
                                />
                            )}
                        >
                            <Label text={Dict.get('Geldiği Tarih')} />
                        </SimpleItem>
                        <SimpleItem dataField="CI_SEANS_SAY" editorType="dxNumberBox"><Label text={Dict.get('Seans')} /></SimpleItem>
                    </GroupItem>
                    <SimpleItem dataField="CI_ACK" editorType="dxTextArea" editorOptions={addMaxLengthToEditorOptions('CARI_ISLEM', 'CI_ACK', { rows: 3 })}><Label text={Dict.get('Açıklama')} /></SimpleItem>
                </Form>
            )}
            {/* Toolbar'a taşındı: Takvim/Liste ve Yenile */}
            {gorunum === 'takvim' && (
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', margin: '6px 0 10px' }}>
                    <SingleButton text={'Yıl'} icon="columnfield" width={70} type={takvimView === 'year' ? 'default' : 'normal'} stylingMode="contained" onClick={() => setTakvimView('year')} />
                    <SingleButton text={'Ay'} icon="event" width={70} type={takvimView === 'month' ? 'default' : 'normal'} stylingMode="contained" onClick={() => setTakvimView('month')} />
                    <SingleButton text={'Hafta'} icon="tableproperties" width={70} type={takvimView === 'week' ? 'default' : 'normal'} stylingMode="contained" onClick={() => setTakvimView('week')} />
                    <SingleButton text={'Gün'} icon="todo" width={70} type={takvimView === 'day' ? 'default' : 'normal'} stylingMode="contained" onClick={() => setTakvimView('day')} />
                </div>
            )}
            {gorunum === 'liste' && !isNewUnsavedRecord && (
                <DataGrid 
                    key={`islem-grid-${casRef}-${islemList.length}`}
                    dataSource={displayIslemList} 
                    showBorders={true} 
                    keyExpr="CI_REF" 
                    focusedRowEnabled={!isCreatingNew}
                    focusedRowKey={isCreatingNew ? null : focusedIslemKey}
                    hoverStateEnabled={false} 
                    repaintChangesOnly={false} 
                    ref={gridRef} 
                    height={resolvedGridHeight} 
                    width={'100%'} 
                    columnAutoWidth={false} 
                    columnMinWidth={120}
                    allowColumnResizing={true}
                    onFocusedRowChanged={(e) => {
                        if (createLockRef.current) return;
                        const selected = e.row?.data || null;
                        if (!selected) return;
                        if (formDirty && !formReadOnly) {
                            if (!window.confirm('Kaydedilmemiş değişiklikler var. Seçilen satıra geçilsin mi?')) {
                                e.component.option('focusedRowKey', focusedIslemKey);
                                return;
                            }
                            setFormDirty(false);
                        }
                        createLockRef.current = false;
                        setActiveIslem(selected);
                        setFocusedIslemKey(selected.CI_REF);
                        setIsCreatingNew(false);
                        setFormReadOnly(true);
                    }}
                    selectedRowKeys={selectedIslemKeys}
                    onSelectionChanged={(e) => setSelectedIslemKeys(e.selectedRowKeys || [])}
                    onRowPrepared={(e) => {
                        if (e.rowType === 'data') {
                            const { CI_ISL_TAR, CI_RAN_TAR } = e.data || {};
                            if (CI_ISL_TAR && CI_RAN_TAR) {
                                const islemTarih = new Date(CI_ISL_TAR);
                                const randevuTarih = new Date(CI_RAN_TAR);
                                if (islemTarih > randevuTarih) {
                                    e.rowElement.style.background = '#d32f2f'; // kırmızı
                                    e.rowElement.style.color = '#fff';
                                    e.rowElement.style.fontWeight = 'bold';
                                } else {
                                    e.rowElement.style.background = 'lime';
                                    e.rowElement.style.color = '#111';
                                    e.rowElement.style.fontWeight = 'bold';
                                }
                            } else {
                                // Diğer satırlar eski gibi
                                e.rowElement.style.background = '';
                                e.rowElement.style.color = '';
                                e.rowElement.style.fontWeight = '';
                            }
                        }
                    }}
                    onEditorPreparing={(e) => {
                        if (e.parentType !== 'dataRow') return;
                        if (e.dataField !== 'CI_ISL_TAR' && e.dataField !== 'CI_RAN_TAR') return;
                        const prevHandler = e.editorOptions?.onValueChanged;
                        e.editorOptions.onValueChanged = (args) => {
                            if (typeof prevHandler === 'function') prevHandler(args);
                            const rowData = e.row?.data;
                            if (!rowData) return;
                            const prevVal = rowData[e.dataField];
                            void applyFormDateChange(e.dataField, args.value, prevVal);
                        };
                    }}
                >
                    <Selection mode="multiple" showCheckBoxesMode="always" selectAllMode="page" />
                    <GridToolbar>
                        <GridToolbarItem
                            location="before"
                            widget="dxSelectBox"
                            options={{
                                dataSource: sablonComboItems(islemsablonLkp?.baslik, satisTipi, satisTuru, showAllSablon),
                                displayExpr: 'IS_AD',
                                valueExpr: 'IS_AD',
                                value: selectedSablon,
                                onValueChanged: (e) => setSelectedSablon(e.value),
                                disabled: isNewUnsavedRecord || !gridReadOnly,
                                width: 220,
                                placeholder: 'Şablon Seç',
                                inputAttr: { 'aria-label': 'Şablon' },
                            }}
                        />
                        <GridToolbarItem
                            location="before"
                            render={() => (
                                <label className="d-inline-flex align-items-center gap-1 mb-0" style={{ minHeight: 42, cursor: 'pointer' }}>
                                    <input
                                        type="checkbox"
                                        aria-label="Tümü"
                                        checked={showAllSablon}
                                        disabled={isNewUnsavedRecord || !gridReadOnly}
                                        onChange={(event) => {
                                            setShowAllSablon(event.target.checked);
                                            setSelectedSablon(null);
                                        }}
                                        style={{ width: 18, height: 18 }}
                                    />
                                    Tümü
                                </label>
                            )}
                        />
                        <GridToolbarItem
                            location="before"
                            widget="dxButton"
                            options={{
                                text: 'Şablonu Uygula',
                                icon: 'check',
                                type: 'default',
                                stylingMode: 'contained',
                                onClick: () => applySablon(),
                                disabled: isNewUnsavedRecord || !selectedSablon || !gridReadOnly || sablonApplying || islemReadOnly
                            }}
                        />
                    </GridToolbar>
                    <Paging enabled={false} />
                    <Editing mode="batch" allowUpdating={false} allowAdding={false} allowDeleting={false} selectTextOnEditStart={true} startEditAction={'click'} />
                    <Scrolling mode="standard" useNative={false} showScrollbar="always" columnRenderingMode="standard" />
                    <LoadPanel enabled={true} />
                    <Column dataField="CI_REF" visible={false} caption={Dict.get('K.No')} allowEditing={false} />
                    <Column
                        dataField="_GERCEKLESTI"
                        caption="Gerçekleşti"
                        dataType="boolean"
                        width={220}
                        minWidth={180}
                        alignment="left"
                        allowSorting={false}
                        allowFiltering={false}
                        allowEditing={!islemReadOnly}
                        cellRender={({ data }) => (
                            <IslemGerceklestiCell
                                data={data}
                                readOnly={islemReadOnly || (Number(data?.CI_REF) > 0 && !canDeleteByOwner(glb.default?.KULLANICI, islemIsYapanRef(data)))}
                                islemList={islemList}
                                casRef={casRef}
                                sablonLines={islemsablonLkp?.detay || []}
                                onPersistUpdates={persistIslemUpdatesFromGrid}
                            />
                        )}
                    />
                    <Column dataField="CI_TUR" caption={Dict.get('Paket İçeriği')}>
                        <Lookup dataSource={filterArray(glb.default.sabitLkp, x => x.ST_TUR === satistipigrubu)} searchEnabled={true} searchMode='contains' displayExpr='ST_AD' searchExpr='ST_AD' valueExpr='ST_KODINT' />
                    </Column>
                    <Column dataField="CI_RAN_TAR" dataType="date" format="dd-MM-yyyy" caption={Dict.get('Randevu Tarihi')} />
                    <Column dataField="CI_ISL_TAR" dataType="date" format="dd-MM-yyyy" caption={Dict.get('Geldiği Tarih')} />
                    <Column dataField="CI_GIR_KUL" caption={Dict.get('Giren')} allowEditing={false}>
                        <Lookup dataSource={asArray(glb.default.KULLANICILkp)} searchEnabled={true} searchMode='contains' displayExpr='KUL_ADI' searchExpr='KUL_ADI' valueExpr='KUL_REF' />
                    </Column>
                    <Column dataField="CI_UPD_KUL" caption={Dict.get('Yapan')} allowEditing={false}>
                        <Lookup dataSource={asArray(glb.default.KULLANICILkp)} searchEnabled={true} searchMode='contains' displayExpr='KUL_ADI' searchExpr='KUL_ADI' valueExpr='KUL_REF' />
                    </Column>
                    <Column {...addMaxLengthToGridColumn('CARI_ISLEM', 'CI_ACK', {
                    dataField: "CI_ACK", 
                    caption: Dict.get('Açıklama')
                  })} />
                </DataGrid>
            )}
            {gorunum === 'takvim' && takvimView !== 'year' && !isNewUnsavedRecord && (
                <Scheduler
                    dataSource={schedulerData}
                    views={[takvimView]}
                    currentView={takvimView}
                    currentDate={schedulerDate}
                    height={500}
                    startDateExpr={'startDate'}
                    endDateExpr={'endDate'}
                    allDayExpr={'allDay'}
                    textExpr={'paketText'}
                    showAllDayPanel={true}
                    useDropDownViewSwitcher={false}
                    onCurrentViewChange={(v) => setTakvimView(v)}
                    onCurrentDateChange={(d) => setSchedulerDate(d)}
                    onAppointmentRendered={(e) => {
                        const done = !!e.appointmentData?.CI_ISL_TAR;
                        e.appointmentElement.style.backgroundColor = done ? '#4caf50' : '#1976d2';
                        e.appointmentElement.style.borderColor = done ? '#388e3c' : '#1565c0';
                        const ptext = e.appointmentData?.paketText || '';
                        const dt = e.appointmentData?.startDate ? new Date(e.appointmentData.startDate) : null;
                        const dts = dt ? dt.toLocaleDateString() : '';
                        const ack = e.appointmentData?.CI_ACK ? ` - ${e.appointmentData.CI_ACK}` : '';
                        e.appointmentElement.title = `${ptext} ${dts}${ack}`.trim();
                    }}
                >
                    <View type={'month'} intervalCount={12} />
                    <View type={'week'} />
                    <View type={'day'} />
                </Scheduler>
            )}
            {gorunum === 'takvim' && takvimView === 'year' && !isNewUnsavedRecord && (
                <div style={{
                    display: 'grid',
                    gridTemplateColumns: 'repeat(auto-fill,minmax(250px,1fr))',
                    gap: '8px',
                    width: '100%',
                    maxHeight: '70vh',
                    overflowY: 'auto',
                    paddingBottom: '8px'
                }}>
                    {Array.from({ length: 13 }).map((_, idx) => {
                        const base = schedulerDate || firstApptMonth;
                        const y = base.getFullYear();
                        const mDate = new Date(y, base.getMonth() + idx, 1);
                        const monthItems = schedulerData.filter(r => {
                            const d = new Date(r.CI_RAN_TAR);
                            return d.getFullYear() === mDate.getFullYear() && d.getMonth() === mDate.getMonth();
                        });
                        return (
                            <div key={idx} style={{ border: '1px solid #ccc', borderRadius: 4, padding: 4, background: '#fafafa' }}>
                                <div style={{ fontWeight: '600', fontSize: 13, marginBottom: 4 }}>
                                    {mDate.toLocaleDateString(undefined, { month: 'long', year: 'numeric' })}
                                </div>
                                {monthItems.length === 0 && <div style={{ fontSize: 12, color: '#777' }}>Kayıt yok</div>}
                                {monthItems.map(mi => {
                                    const done = !!mi.CI_ISL_TAR;
                                    const dObj = new Date(mi.CI_RAN_TAR);
                                    const day = dObj.getDate();
                                    const wday = dObj.toLocaleDateString('tr-TR', { weekday: 'short' });
                                    return (
                                        <div key={mi.CI_REF + '_' + day} style={{
                                            fontSize: 12,
                                            marginBottom: 2,
                                            padding: '2px 4px',
                                            borderRadius: 3,
                                            background: done ? '#4caf50' : '#1976d2',
                                            color: '#fff',
                                            display: 'flex',
                                            justifyContent: 'space-between'
                                        }} title={`${mi.paketText} - ${new Date(mi.CI_RAN_TAR).toLocaleDateString('tr-TR')} (${wday})${mi.CI_ACK ? ' - ' + mi.CI_ACK : ''}`}>
                                            <span>{day}. ({wday}) {mi.paketText}</span>
                                            {done && <span style={{ fontWeight: 'bold' }}>✔</span>}
                                        </div>
                                    );
                                })}
                            </div>
                        );
                    })}
                </div>
            )}
        </div>
    );
}
